import { Provider } from 'mobx-react';
import * as React from 'react';
import { Switch, useLocation, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import {
	IImpersonationContextComponentProps,
	ILocationState,
	IMultiCampaignSchedulerRequest,
	IMultiCampaignSchedulerResult,
} from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { CampaignCalendarSuggestionsProvider } from '../../../../web/components/campaigns/CampaignCalendar/context';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { MultiBlogScheduler } from '../../../../web/containers/MultiCampaignScheduler/components/blogs/MultiBlogScheduler';
import { MultiSocialCampaignSchedulerComplete } from '../../../../web/containers/MultiCampaignScheduler/components/social/MultiSocialCampaignSchedulerComplete';
import { MultiSocialScheduler } from '../../../../web/containers/MultiCampaignScheduler/components/social/MultiSocialScheduler';
import { AccountContentCalendarBlogScheduleSuccess } from '../../blogs/AccountContentCalendarBlogScheduleSuccess';

export function AccountMultiCampaignSchedulerRoutes() {
	const userSession = useUserSession();
	const { url } = useRouteMatch();
	const history = useHistory();

	const location = useLocation<ILocationState<any, IMultiCampaignSchedulerRequest>>();
	const request = location.state?.model;
	const impersonationContextComponentPropsRef = React.useRef<IImpersonationContextComponentProps>({
		impersonationContext: new ImpersonationContextViewModel(request?.impersonationContext),
	});
	const onFinishRef = React.useRef(request?.onFinish);

	const onScheduleSuccess = React.useCallback(
		(e: CustomEvent<IMultiCampaignSchedulerResult>) => {
			const { withCompliance } = e.detail;
			if (location.pathname.startsWith(`${url}/blogs`)) {
				e.preventDefault();
				history.push({
					pathname: withCompliance ? `${url}/blogs/compliance-confirmation` : `${url}/blogs/success`,
					state: location.state,
				});
			} else if (location.pathname.startsWith(`${url}/social`)) {
				e.preventDefault();
				history.push(`${url}/social/success`);
			}

			onFinishRef.current?.();
		},
		[history, location.pathname, location.state, url]
	);

	return (
		<Provider {...impersonationContextComponentPropsRef.current}>
			<CampaignCalendarSuggestionsProvider
				initialSelectedSuggestions={request?.suggestions}
				supportsBulkScheduleSuggestions
				onSuccess={onScheduleSuccess}
			>
				<Switch>
					<PrivateRoute path={`${url}/blogs/success`} userSession={userSession}>
						<AccountContentCalendarBlogScheduleSuccess multiple />
					</PrivateRoute>
					<PrivateRoute path={`${url}/blogs`} userSession={userSession}>
						<MultiBlogScheduler />
					</PrivateRoute>
					<PrivateRoute path={`${url}/social/success`} userSession={userSession}>
						<MultiSocialCampaignSchedulerComplete />
					</PrivateRoute>
					<PrivateRoute path={`${url}/social`} userSession={userSession}>
						<MultiSocialScheduler />
					</PrivateRoute>
				</Switch>
			</CampaignCalendarSuggestionsProvider>
		</Provider>
	);
}
